/**
 * Shared momentum scroll logic for horizontal drag-scroll (orders and employee table utils).
 */
import { ElementRef } from '@angular/core';

export interface MomentumScrollConfig {
  scrollWrapper: ElementRef<HTMLElement> | ElementRef;
  velocityX: number;
  momentumAnimation: number | null;
}

/** Config shape for horizontal drag move. Used by employee and orders TableUtils. */
export interface HorizontalDragMoveConfig {
  scrollWrapper: ElementRef;
  startX: number;
  scrollLeft: number;
  lastX: number;
  lastMoveTime: number;
  velocityX: number;
}

/**
 * Apply horizontal drag move: update scroll position and velocity. Shared by employee and orders table.utils.
 */
export function applyHorizontalDragMove(pageX: number, config: HorizontalDragMoveConfig): void {
  const el = config.scrollWrapper.nativeElement;
  const x = pageX - el.offsetLeft;
  const currentTime = Date.now();
  const timeDelta = currentTime - config.lastMoveTime;
  if (timeDelta > 0) {
    config.velocityX = (x - config.lastX) / timeDelta;
  }
  const walk = (x - config.startX) * 1.5;
  requestAnimationFrame(() => {
    el.scrollLeft = config.scrollLeft - walk;
  });
  config.lastX = x;
  config.lastMoveTime = currentTime;
}

const MIN_VELOCITY = 0.1;
const FRICTION = 0.95;
const MIN_VELOCITY_THRESHOLD = 0.01;

/**
* Starts a momentum-based horizontal scrolling animation using the configuration's velocity.
* @example
* startMomentumScrolling(config)
* undefined
* @param {MomentumScrollConfig} config - Configuration object containing velocityX, scrollWrapper (with nativeElement), momentumAnimation storage, and related settings.
* @returns {void} No return value; initiates/updates animation via requestAnimationFrame.
**/
export function startMomentumScrolling(config: MomentumScrollConfig): void {
  if (Math.abs(config.velocityX) < MIN_VELOCITY) {
    return;
  }

  const animate = () => {
    if (Math.abs(config.velocityX) < MIN_VELOCITY_THRESHOLD) {
      stopMomentumScrolling(config);
      return;
    }

    config.scrollWrapper.nativeElement.scrollLeft -= config.velocityX;
    config.velocityX *= FRICTION;

    config.momentumAnimation = requestAnimationFrame(animate);
  };

  config.momentumAnimation = requestAnimationFrame(animate);
}

export function stopMomentumScrolling(config: MomentumScrollConfig): void {
  if (config.momentumAnimation) {
    cancelAnimationFrame(config.momentumAnimation);
    config.momentumAnimation = null;
  }
}
